import { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface TagType {
  id: string;
  name: string;
  color?: string;
}

interface TagSelectorProps {
  trigger: React.ReactNode;
  selectedTags: TagType[];
  availableTags: TagType[];
  organizationId: string;
  onTagsChange: (tags: TagType[]) => void;
  onTagCreated?: () => void;
  align?: 'start' | 'center' | 'end';
}

export function TagSelector({
  trigger,
  selectedTags,
  availableTags,
  organizationId,
  onTagsChange,
  onTagCreated,
  align = 'end',
}: TagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [options, setOptions] = useState<TagType[]>(availableTags);
  const [isSearching, setIsSearching] = useState(false);

  const normalize = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  const normalizedQuery = normalize(query);

  // Debounced search
  useEffect(() => {
    if (!open || !organizationId) return;
    
    const searchTags = async () => {
      setIsSearching(true);
      try {
        let tagQuery = supabase
          .from('tags')
          .select('id, name, color')
          .eq('organization_id', organizationId)
          .order('name');
        
        // Add search filter if query exists
        if (normalizedQuery) {
          tagQuery = tagQuery.ilike('name', `%${normalizedQuery}%`);
        }
        
        const { data } = await tagQuery.limit(100);
        if (data) setOptions(data);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(searchTags, 300);
    return () => clearTimeout(timeoutId);
  }, [open, organizationId, normalizedQuery]);

  const toggleTag = (tag: TagType) => {
    const isSelected = selectedTags.some(t => t.id === tag.id);
    if (isSelected) {
      onTagsChange(selectedTags.filter(t => t.id !== tag.id));
    } else {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const createNewTag = async () => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || !organizationId) return;

    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('tags')
        .insert({
          name: trimmedQuery,
          organization_id: organizationId,
          color: `hsl(${Math.random() * 360}, 70%, 50%)`,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const newTag = { id: data.id, name: data.name, color: data.color } as TagType;
        onTagsChange([...selectedTags, newTag]);
        setOptions(prev => (prev.some(t => t.id === newTag.id) ? prev : [...prev, newTag]));
        setQuery('');
        toast.success(`Tag "${trimmedQuery}" created`);
        onTagCreated?.();
      }
    } catch (error: any) {
      console.error('Error creating tag:', error);
      if (error?.code === '23505') {
        // Tag already exists in this organization, select it instead
        const { data: existing } = await supabase
          .from('tags')
          .select('id, name, color')
          .eq('organization_id', organizationId)
          .eq('name', trimmedQuery)
          .maybeSingle();
        if (existing) {
          const existingTag = { id: existing.id, name: existing.name, color: (existing as any).color } as TagType;
          onTagsChange([...selectedTags.filter(t => t.id !== existingTag.id), existingTag]);
          setOptions(prev => (prev.some(t => t.id === existingTag.id) ? prev : [...prev, existingTag]));
          setQuery('');
          toast.success(`Tag "${trimmedQuery}" added`);
          onTagCreated?.();
        } else {
          toast.info('Tag already exists');
        }
      } else {
        toast.error(error.message || 'Failed to create tag');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const filteredTags = options;

  const exactMatch = options.find(tag => normalize(tag.name) === normalizedQuery);
  const showCreateOption = normalizedQuery && !exactMatch;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="z-50 bg-popover shadow-md w-80 p-0" align={align}>
        <div className="flex flex-col">
          <div className="p-3 border-b">
            <h4 className="font-medium text-sm">Tags</h4>
            {selectedTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selectedTags.map((tag) => {
                  const baseColor = tag.color || 'hsl(var(--primary))';
                  const transparentBg = baseColor.startsWith('hsl')
                    ? baseColor.replace(')', ' / 0.2)')
                    : baseColor + '33';
                  const borderColor = baseColor;
                  
                  return (
                    <div
                      key={tag.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border-2"
                      style={{ 
                        backgroundColor: transparentBg,
                        borderColor: borderColor,
                        color: 'white'
                      }}
                    >
                      <span>{tag.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onTagsChange(selectedTags.filter(t => t.id !== tag.id));
                        }}
                        className="opacity-70 hover:opacity-100 transition-fast"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or create tags"
              className="h-9"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {showCreateOption && (
                <CommandGroup>
                  <CommandItem
                    onSelect={createNewTag}
                    disabled={isCreating}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create "{query.trim()}"
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandEmpty>No tags found.</CommandEmpty>
              <CommandGroup>
                {filteredTags.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    value={normalize(tag.name)}
                    onSelect={() => toggleTag(tag)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {tag.color && (
                        <div
                          className="w-3 h-3 rounded-full shrink-0"
                          style={{ backgroundColor: tag.color }}
                        />
                      )}
                      {tag.name.trim()}
                    </div>
                    <div
                      className={cn(
                        "ml-auto h-4 w-4 rounded-sm border border-primary",
                        selectedTags.some(t => t.id === tag.id)
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50"
                      )}
                    >
                      {selectedTags.some(t => t.id === tag.id) && (
                        <X className="h-3 w-3" />
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
